import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Schedule as ScheduleIcon,
  Phone as ContactIcon,
  AccountTree as ClusterIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import type { Location } from '../../types/vrp';
import { useAccessibility } from '../../hooks/useAccessibility';

interface ViewLocationDialogProps {
  open: boolean;
  location: Location | null;
  onClose: () => void;
  onEdit: (location: Location) => void;
}

export const ViewLocationDialog: React.FC<ViewLocationDialogProps> = React.memo(({
  open,
  location,
  onClose,
  onEdit,
}) => {
  // Early return for null location
  if (!location) return null;

  // Accessibility hook for comprehensive ARIA support
  const { announce, getAriaProps } = useAccessibility();

  // State for copy feedback
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Refs for focus management
  const dialogRef = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  // Generate unique IDs for ARIA relationships
  const dialogId = `location-dialog-${location.id}`;
  const titleId = `location-dialog-title-${location.id}`;
  const descriptionId = `location-dialog-description-${location.id}`;

  // Focus management on dialog open/close
  useEffect(() => {
    if (open) {
      // Announce dialog opening to screen readers
      announce(`Location details dialog opened for ${location.name || 'unnamed location'}`, 'polite');
      
      // Set initial focus to the dialog after a short delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (dialogRef.current) {
          dialogRef.current.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open, location.name, announce]);

  // Keyboard navigation handler
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          announce('Location details dialog closed', 'polite');
          break;
        case 'Tab':
          // Let default tab behavior handle focus management within dialog
          break;
        case 'Enter':
          // If focus is on the edit button, trigger edit action
          if (document.activeElement?.getAttribute('data-action') === 'edit') {
            event.preventDefault();
            handleEdit();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, announce]);

  // Memoized edit handler for performance optimization
  const handleEdit = useCallback(() => {
    if (location) {
      announce(`Opening edit form for ${location.name || 'location'}`, 'polite');
      onEdit(location);
      onClose();
    }
  }, [location, onEdit, onClose, announce]);

  // Copy to clipboard functionality with accessibility support
  const handleCopy = useCallback(async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      announce(`${fieldName} copied to clipboard: ${text}`, 'polite');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      announce(`Failed to copy ${fieldName} to clipboard`, 'assertive');
    }
  }, [announce]);

  // Memoized location type chip generator with enhanced styling and accessibility
  const getLocationTypeChip = useCallback((type?: string) => {
    const isWarehouse = type === 'warehouse';
    const isCustomer = type === 'customer';
    const label = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Customer';
    
    return (
      <Chip 
        icon={isWarehouse ? <BusinessIcon aria-hidden="true" /> : <LocationIcon aria-hidden="true" />}
        label={label}
        color={isWarehouse ? 'primary' : isCustomer ? 'secondary' : 'default'}
        size="medium"
        sx={{ 
          fontWeight: 'medium',
          '& .MuiChip-icon': { fontSize: '1.1rem' }
        }}
        {...getAriaProps({
          label: `Location type: ${label}`,
        })}
        role="status"
        data-keyboard-navigable="true"
      />
    );
  }, [getAriaProps]);

  // Enhanced coordinate formatting with copy functionality
  const formatCoordinates = useCallback((lat?: number, lon?: number) => {
    if (typeof lat !== 'number' || typeof lon !== 'number') {
      return 'Coordinates not available';
    }
    return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
  }, []);

  // Safe date formatting with enhanced display
  const formatDate = useCallback((dateString?: string) => {
    if (!dateString) return 'Not available';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date format error';
    }
  }, []);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      ref={dialogRef}
      {...getAriaProps({
        role: 'dialog',
        label: `Location details for ${location.name || 'unnamed location'}`,
        describedBy: descriptionId,
      })}
      aria-labelledby={titleId}
      aria-modal="true"
      data-keyboard-navigation-group="true"
    >
      <DialogTitle id={titleId}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <LocationIcon 
              sx={{ mr: 1.5, color: 'primary.main' }} 
              aria-hidden="true"
            />
            <Box>
              <Typography 
                variant="h6" 
                component="h2" 
                fontWeight="bold"
                role="heading"
                aria-level={1}
              >
                {location.name || 'Unnamed Location'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mt: 0.5 }}
                id={descriptionId}
              >
                Location Details
              </Typography>
            </Box>
          </Box>
          {getLocationTypeChip(location.location_type)}
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }} data-keyboard-navigation-group="true">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Basic Information Section */}
          <Card 
            variant="outlined" 
            sx={{ borderRadius: 2 }}
            role="region"
            aria-labelledby="basic-info-heading"
            data-keyboard-navigable="true"
          >
            <CardContent sx={{ pb: '16px !important' }}>
              <Typography 
                id="basic-info-heading"
                variant="subtitle1" 
                fontWeight="bold" 
                color="primary" 
                gutterBottom
                role="heading"
                aria-level={2}
              >
                Basic Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box role="group" aria-labelledby="location-id-label">
                  <Typography 
                    id="location-id-label"
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                  >
                    ID
                  </Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight="medium"
                    aria-describedby="location-id-label"
                  >
                    {location.id ?? 'Not available'}
                  </Typography>
                </Box>

                {location.address && (
                  <Box role="group" aria-labelledby="location-address-label">
                    <Typography 
                      id="location-address-label"
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                    >
                      Address
                    </Typography>
                    <Typography 
                      variant="body1"
                      aria-describedby="location-address-label"
                    >
                      {location.address}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Location Details Section */}
          {(location.description || location.operating_hours || location.contact_info) && (
            <Card 
              variant="outlined" 
              sx={{ borderRadius: 2 }}
              role="region"
              aria-labelledby="additional-details-heading"
              data-keyboard-navigable="true"
            >
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography 
                  id="additional-details-heading"
                  variant="subtitle1" 
                  fontWeight="bold" 
                  color="primary" 
                  gutterBottom
                  role="heading"
                  aria-level={2}
                >
                  Additional Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  
                  {location.description && (
                    <Box role="group" aria-labelledby="location-description-label">
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <InfoIcon sx={{ fontSize: '1.1rem', mr: 0.5, color: 'text.secondary' }} aria-hidden="true" />
                        <Typography 
                          id="location-description-label"
                          variant="body2" 
                          color="text.secondary"
                        >
                          Description
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body1" 
                        sx={{ ml: 2.5 }}
                        aria-describedby="location-description-label"
                      >
                        {location.description}
                      </Typography>
                    </Box>
                  )}

                  {location.operating_hours && (
                    <Box role="group" aria-labelledby="location-hours-label">
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: '1.1rem', mr: 0.5, color: 'text.secondary' }} aria-hidden="true" />
                        <Typography 
                          id="location-hours-label"
                          variant="body2" 
                          color="text.secondary"
                        >
                          Operating Hours
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body1" 
                        sx={{ ml: 2.5 }}
                        aria-describedby="location-hours-label"
                      >
                        {location.operating_hours}
                      </Typography>
                    </Box>
                  )}

                  {location.contact_info && (
                    <Box role="group" aria-labelledby="location-contact-label">
                      <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                        <ContactIcon sx={{ fontSize: '1.1rem', mr: 0.5, color: 'text.secondary' }} aria-hidden="true" />
                        <Typography 
                          id="location-contact-label"
                          variant="body2" 
                          color="text.secondary"
                        >
                          Contact Information
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body1" 
                        sx={{ ml: 2.5 }}
                        aria-describedby="location-contact-label"
                      >
                        {location.contact_info}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Coordinates Section */}
          <Card 
            variant="outlined" 
            sx={{ borderRadius: 2 }}
            role="region"
            aria-labelledby="geographic-info-heading"
            data-keyboard-navigable="true"
          >
            <CardContent sx={{ pb: '16px !important' }}>
              <Typography 
                id="geographic-info-heading"
                variant="subtitle1" 
                fontWeight="bold" 
                color="primary" 
                gutterBottom
                role="heading"
                aria-level={2}
              >
                Geographic Information
              </Typography>
              <Box>
                {(location.location_lon && location.location_lat) ? (
                  <Box role="group" aria-labelledby="coordinates-label">
                    <Typography 
                      id="coordinates-label"
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                    >
                      Coordinates
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography 
                        variant="body1" 
                        fontFamily="monospace" 
                        sx={{ flexGrow: 1 }}
                        aria-describedby="coordinates-label"
                      >
                        {formatCoordinates(location.location_lat, location.location_lon)}
                      </Typography>
                      <Tooltip title={copiedField === 'coordinates' ? 'Copied!' : 'Copy coordinates'}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleCopy(
                            formatCoordinates(location.location_lat, location.location_lon), 
                            'coordinates'
                          )}
                          sx={{ color: copiedField === 'coordinates' ? 'success.main' : 'text.secondary' }}
                          aria-label={`Copy coordinates ${formatCoordinates(location.location_lat, location.location_lon)} to clipboard`}
                          data-keyboard-navigable="true"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : (
                  <Box role="group" aria-labelledby="coordinates-unavailable-label">
                    <Typography 
                      id="coordinates-unavailable-label"
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                    >
                      Coordinates
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ fontStyle: 'italic' }}
                      aria-describedby="coordinates-unavailable-label"
                      role="status"
                    >
                      Not available
                    </Typography>
                  </Box>
                )}

                {location.location_clusters && (
                  <Box sx={{ mt: 2 }} role="group" aria-labelledby="cluster-label">
                    <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                      <ClusterIcon sx={{ fontSize: '1.1rem', mr: 0.5, color: 'text.secondary' }} aria-hidden="true" />
                      <Typography 
                        id="cluster-label"
                        variant="body2" 
                        color="text.secondary"
                      >
                        Location Cluster
                      </Typography>
                    </Box>
                    <Chip
                      label={location.location_clusters.name}
                      variant="outlined"
                      size="small"
                      sx={{ ml: 2.5, fontWeight: 'medium' }}
                      aria-describedby="cluster-label"
                      data-keyboard-navigable="true"
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Timestamps Section */}
          <Card 
            variant="outlined" 
            sx={{ borderRadius: 2 }}
            role="region"
            aria-labelledby="system-info-heading"
            data-keyboard-navigable="true"
          >
            <CardContent sx={{ pb: '16px !important' }}>
              <Typography 
                id="system-info-heading"
                variant="subtitle1" 
                fontWeight="bold" 
                color="primary" 
                gutterBottom
                role="heading"
                aria-level={2}
              >
                System Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box role="group" aria-labelledby="created-label">
                  <Typography 
                    id="created-label"
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                  >
                    Created
                  </Typography>
                  <Typography 
                    variant="body1"
                    aria-describedby="created-label"
                  >
                    {formatDate(location.created_at)}
                  </Typography>
                </Box>

                <Box role="group" aria-labelledby="updated-label">
                  <Typography 
                    id="updated-label"
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                  >
                    Last Updated
                  </Typography>
                  <Typography 
                    variant="body1"
                    aria-describedby="updated-label"
                  >
                    {formatDate(location.updated_at)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>
      
      <DialogActions role="group" aria-label="Dialog actions">
        <Button 
          onClick={onClose}
          aria-label={`Close location details dialog for ${location.name || 'unnamed location'}`}
          data-keyboard-navigable="true"
        >
          Close
        </Button>
        <Button 
          variant="outlined" 
          onClick={handleEdit}
          startIcon={<EditIcon aria-hidden="true" />}
          aria-label={`Edit location ${location.name || 'unnamed location'}`}
          data-keyboard-navigable="true"
          data-action="edit"
        >
          Edit
        </Button>
      </DialogActions>

      {/* Live region for accessibility announcements */}
      <Box
        id={`${dialogId}-live-region`}
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {/* Screen reader announcements will be inserted here */}
      </Box>
    </Dialog>
  );
});

ViewLocationDialog.displayName = 'ViewLocationDialog';
